import React, { useState } from 'react';
import { Plus, Trash, Edit, Info } from 'lucide-react';
export type PropertyType = 'Text' | 'Number' | 'Boolean' | 'Date and time';
export interface OutputProperty {
  id: string;
  name: string;
  type: PropertyType;
  description: string;
}
interface OutputDataStructureProps {
  properties: OutputProperty[];
  onPropertiesChange: (properties: OutputProperty[]) => void;
  readOnly?: boolean;
}
export const OutputDataStructure: React.FC<OutputDataStructureProps> = ({
  properties,
  onPropertiesChange,
  readOnly = false
}) => {
  const [isAddingProperty, setIsAddingProperty] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
  const [newProperty, setNewProperty] = useState<Omit<OutputProperty, 'id'>>({
    name: '',
    type: 'Text',
    description: ''
  });
  const handleAddProperty = () => {
    if (!newProperty.name.trim()) return;
    if (editingPropertyId) {
      // Update existing property
      const updatedProperties = properties.map(prop => prop.id === editingPropertyId ? {
        ...prop,
        ...newProperty
      } : prop);
      onPropertiesChange(updatedProperties);
      setEditingPropertyId(null);
    } else {
      // Add new property
      const newPropertyWithId: OutputProperty = {
        ...newProperty,
        id: Date.now().toString()
      };
      onPropertiesChange([...properties, newPropertyWithId]);
    }
    // Reset form
    setNewProperty({
      name: '',
      type: 'Text',
      description: ''
    });
    setIsAddingProperty(false);
  };
  const handleCancelAdd = () => {
    setIsAddingProperty(false);
    setEditingPropertyId(null);
    setNewProperty({
      name: '',
      type: 'Text',
      description: ''
    });
  };
  const handleDeleteProperty = (id: string) => {
    onPropertiesChange(properties.filter(prop => prop.id !== id));
  };
  const handleEditProperty = (property: OutputProperty) => {
    setNewProperty({
      name: property.name,
      type: property.type,
      description: property.description
    });
    setEditingPropertyId(property.id);
    setIsAddingProperty(true);
  };
  return <div className="border border-gray-200 rounded-md mb-6" data-id="element-1317">
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-1318">
        <h2 className="text-sm font-medium text-gray-800" data-id="element-1319">
          Output data structure
        </h2>
        <Info className="w-4 h-4 text-gray-400" data-id="element-1320" />
      </div>
      <div className="p-3" data-id="element-1321">
        <p className="text-sm text-gray-600 mb-4" data-id="element-1322">
          Define the expected data output, which can be used in subsequent steps
          of this automation.
        </p>
        {/* List of existing properties */}
        {properties.length > 0 && <div className="mb-4" data-id="element-1323">
            <div className="grid grid-cols-12 gap-2 p-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500" data-id="element-1324">
              <div className="col-span-4" data-id="element-1325">Property name</div>
              <div className="col-span-3" data-id="element-1326">Type</div>
              <div className="col-span-4" data-id="element-1327">Description</div>
              {!readOnly && <div className="col-span-1" data-id="element-1328">Actions</div>}
            </div>
            {properties.map(property => <div key={property.id} className="grid grid-cols-12 gap-2 p-2 border-b border-gray-100 text-sm text-gray-700 items-center" data-id="element-1329">
                <div className="col-span-4 truncate" data-id="element-1330">{property.name}</div>
                <div className="col-span-3" data-id="element-1331">{property.type}</div>
                <div className={`${readOnly ? 'col-span-5' : 'col-span-4'} truncate`} data-id="element-1332">
                  {property.description}
                </div>
                {!readOnly && <div className="col-span-1 flex space-x-1" data-id="element-1333">
                    <button onClick={() => handleEditProperty(property)} className="text-gray-400 hover:text-gray-600" data-id="element-1334">
                      <Edit className="w-4 h-4" data-id="element-1335" />
                    </button>
                    <button onClick={() => handleDeleteProperty(property.id)} className="text-gray-400 hover:text-red-500" data-id="element-1336">
                      <Trash className="w-4 h-4" data-id="element-1337" />
                    </button>
                  </div>}
              </div>)}
          </div>}
        {/* Add property form */}
        {isAddingProperty && !readOnly ? <div className="border border-gray-200 rounded-md p-3 mb-3" data-id="element-1338">
            <div className="mb-3" data-id="element-1339">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1340">
                Property name <span className="text-gray-500" data-id="element-1341">*</span>
              </label>
              <input type="text" className="w-full p-2 border border-gray-200 rounded-md text-sm" placeholder="Enter property name" value={newProperty.name} onChange={e => setNewProperty({
            ...newProperty,
            name: e.target.value
          })} data-id="element-1342" />
            </div>
            <div className="mb-3" data-id="element-1343">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1344">
                Property type <span className="text-gray-500" data-id="element-1345">*</span>
              </label>
              <select className="w-full p-2 border border-gray-200 rounded-md text-sm appearance-none bg-white" value={newProperty.type} onChange={e => setNewProperty({
            ...newProperty,
            type: e.target.value as PropertyType
          })} data-id="element-1346">
                <option value="Text" data-id="element-1347">Text</option>
                <option value="Number" data-id="element-1348">Number</option>
                <option value="Boolean" data-id="element-1349">Boolean</option>
                <option value="Date and time" data-id="element-1350">Date and time</option>
              </select>
            </div>
            <div className="mb-3" data-id="element-1351">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1352">
                Description <span className="text-gray-500" data-id="element-1353">*</span>
              </label>
              <input type="text" className="w-full p-2 border border-gray-200 rounded-md text-sm" placeholder="Describe what the agent should output" value={newProperty.description} onChange={e => setNewProperty({
            ...newProperty,
            description: e.target.value
          })} data-id="element-1354" />
            </div>
            <div className="flex justify-end mt-3" data-id="element-1355">
              <button onClick={handleCancelAdd} className="px-3 py-1 border border-gray-200 rounded-md text-sm mr-2" data-id="element-1356">
                Cancel
              </button>
              <button onClick={handleAddProperty} className="px-3 py-1 bg-gray-700 text-white rounded-md text-sm" disabled={!newProperty.name.trim() || !newProperty.description.trim()} data-id="element-1357">
                {editingPropertyId ? 'Update' : 'Add'}
              </button>
            </div>
          </div> : !readOnly && <button onClick={() => setIsAddingProperty(true)} className="w-full py-2 border border-gray-200 rounded-md text-sm font-medium flex items-center justify-center" data-id="element-1358">
              <Plus className="w-4 h-4 mr-1" data-id="element-1359" />
              Add property
            </button>}
      </div>
    </div>;
};